// SPDX-License-Identifier: Elastic-2.0
/**
 * pairingMock — MSW handlers + fixtures for the gateway side of A5 (T-018).
 *
 * Half of the original mock is now obsolete: F-200's `/auth/pair/start`
 * + `/auth/pair/verify` ship in main and are called via the generated
 * `PairService` (see `usePairingFlow.ts`). What stays mocked here:
 *
 *  - `POST /api/mirlai/v1/pair` (F-202) — gateway-side device pairing
 *    handshake. F-300 has not yet published this endpoint in its
 *    OpenAPI snapshot at `docs/api/mirlai-v1-openapi.json`, so the
 *    generated gateway client has no method for it. Tests mount this
 *    handler via `msw/node` + `setupServer` (same pattern as before);
 *    dev runs hit the live gateway and 404 until Lane A ships it.
 *  - `POST /api/mirlai/v1/auth/local-token` (F-300) — local-only JWT
 *    issuance for Path B. Same status: not in the OpenAPI snapshot
 *    yet; mocked here meanwhile.
 *
 * Swap plan (final follow-up once F-300 publishes both endpoints):
 *  - Delete this file's `gatewayPair` + `localTokenIssue` wrappers
 *    and the matching MSW handlers.
 *  - Replace the inline `pairingClient.gatewayPair(jwt)` call in
 *    `usePairingFlow.ts` with the generated gateway service method.
 *  - Likewise for any A5 caller that issues a local-only token.
 *
 * See also: ADR-006 (auth topology), functional spec § 3.7.
 */
import { HttpResponse, delay, http } from "msw";

// ── Wire types (mirror the eventual F-300 OpenAPI shapes) ───────────────

/** Request body for `POST /api/mirlai/v1/pair` (F-202). */
export interface GatewayPairRequest {
  jwt: string;
}

/** Response body for `POST /api/mirlai/v1/pair`. */
export interface GatewayPairResponse {
  instance_id: string;
  paired_at: string;
}

/** Response body for `POST /api/mirlai/v1/auth/local-token` (F-300). */
export interface LocalTokenResponse {
  /** Long-lived JWT signed by the local issuer (no refresh). */
  local_jwt: string;
}

// ── Fixtures ────────────────────────────────────────────────────────────

/** Default fixture pair-handshake response (F-202). */
export const FIXTURE_GATEWAY_PAIR: GatewayPairResponse = {
  instance_id: "mirlai-pi-fixture",
  paired_at: "2026-05-17T00:00:00Z",
};

/** Default fixture local-token (F-300). */
export const FIXTURE_LOCAL_TOKEN = "local-jwt-fixture-eyJhbGciOiJIUzI1NiJ9";

// ── Paths ───────────────────────────────────────────────────────────────

/** F-202 local-gateway pairing endpoint. */
export const GATEWAY_PAIR_PATH = "/api/mirlai/v1/pair";

/** F-300 local-token issuer endpoint. */
export const GATEWAY_LOCAL_TOKEN_PATH = "/api/mirlai/v1/auth/local-token";

/** F-348 device_id → account attach endpoint (must match `attachDevice.ts`). */
export const ATTACH_ACCOUNT_PATH = "/me/attach-account";

// ── Thin fetch wrappers (stand in for real generated services) ──────────

/**
 * Lightweight client used by `usePairingFlow` until F-300 publishes
 * the pair + local-token endpoints in its OpenAPI snapshot.
 *
 * Each method maps 1:1 to the eventual generated method so the swap
 * is mechanical.
 */
export const pairingClient = {
  async gatewayPair(jwt: string): Promise<GatewayPairResponse> {
    const res = await fetch(GATEWAY_PAIR_PATH, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ jwt } satisfies GatewayPairRequest),
    });
    if (!res.ok) {
      throw new Error(`gateway/pair failed: ${res.status}`);
    }
    return (await res.json()) as GatewayPairResponse;
  },

  async localTokenIssue(): Promise<LocalTokenResponse> {
    const res = await fetch(GATEWAY_LOCAL_TOKEN_PATH, { method: "POST" });
    if (!res.ok) {
      throw new Error(`local-token failed: ${res.status}`);
    }
    return (await res.json()) as LocalTokenResponse;
  },
};

// ── MSW handlers ────────────────────────────────────────────────────────

/**
 * Default handler set for the gateway side. Tests still using the
 * `pairingHandlers` import keep working — the F-200 side just isn't
 * here anymore (mock those via `vi.spyOn(PairService, ...)` instead).
 */
export const pairingHandlers = [
  http.post(GATEWAY_PAIR_PATH, async () => {
    // Tiny latency so optimistic UI has something to react to.
    await delay(50);
    return HttpResponse.json<GatewayPairResponse>(FIXTURE_GATEWAY_PAIR);
  }),

  http.post(GATEWAY_LOCAL_TOKEN_PATH, async () => {
    await delay(50);
    return HttpResponse.json<LocalTokenResponse>({ local_jwt: FIXTURE_LOCAL_TOKEN });
  }),

  // F-348: device_id → account attach (web-slice spec §7.4). Mocked
  // until lane-A ships `POST /me/attach-account`.
  http.post(ATTACH_ACCOUNT_PATH, async () => {
    await delay(20);
    return new HttpResponse(null, { status: 200 });
  }),
];
